import React from 'react';
import {
    Box,
    Card,
    Typography,
    useMediaQuery,
    styled
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ConstructionIcon from '@mui/icons-material/Construction';
import { getCardColor } from '../../utils/colorUtils';
import { BRAND_TYPE } from '../../config';
import LogoSection from '../../components/Logo';

// Styled components for consistent responsive behavior
const ResponsiveCard = styled(Card)(({ theme }) => ({
    width: '550px',
    maxWidth: '95%',
    padding: '48px 24px',
    boxShadow: theme.customShadows?.z1 || '0 8px 40px -12px rgba(0,0,0,0.1)',
    borderRadius: theme.borderRadius?.sm || '12px',
    '@media (min-width: 2560px)': {
        width: '900px',
        padding: '80px 48px',
        borderRadius: '20px',
    },
    '@media (min-width: 3800px)': {
        width: '1300px',
        padding: '120px 72px',
        borderRadius: '28px',
    }
}));

const LogoBox = styled(Box)(({ theme }) => ({
    transform: 'scale(1)',
    transition: 'transform 0.3s ease',
    '@media (min-width: 2560px)': {
        transform: 'scale(1.75)',
    },
    '@media (min-width: 3800px)': {
        transform: 'scale(2.5)',
    }
}));

const IconBox = styled(Box)(({ theme }) => ({
    fontSize: '80px',
    '@media (min-width: 2560px)': {
        fontSize: '140px',
    },
    '@media (min-width: 3800px)': {
        fontSize: '200px',
    }
}));

const Maintenance = () => {
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';

    // Media queries for responsive design
    const is2K = useMediaQuery('(min-width: 2560px)');
    const is4K = useMediaQuery('(min-width: 3800px)');

    const getBrandName = () => {
        switch (BRAND_TYPE) {
            case 'coro':
                return 'Coro';
            case 'censornet':
                return 'Trustlayer';
            case 'boxphish':
            default:
                return 'Boxphish';
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: is4K ? 8 : is2K ? 6 : 3,
            backgroundColor: theme.palette.background.default,
            gap: is4K ? 4 : is2K ? 3 : 2
        }}>
            {/* Maintenance Card */}
            <ResponsiveCard sx={{
                backgroundColor: getCardColor(lightMode),
                textAlign: 'center'
            }}>
                {/* Logo Section */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: is4K ? 8 : is2K ? 6 : 4
                }}>
                    <LogoBox>
                        <LogoSection />
                    </LogoBox>
                </Box>

                {/* Icon */}
                <IconBox sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: is4K ? 5 : is2K ? 4 : 3,
                    color: theme.palette.warning.main
                }}>
                    <ConstructionIcon fontSize="inherit" />
                </IconBox>

                {/* Main Heading */}
                <Typography
                    variant="h1"
                    sx={{
                        mb: is4K ? 4 : is2K ? 3 : 2,
                        fontWeight: 700,
                        fontSize: is4K ? '3.5rem' : is2K ? '2.5rem' : '2rem',
                        color: theme.palette.text.primary
                    }}
                >
                    Under Maintenance
                </Typography>

                {/* Description */}
                <Typography
                    variant="h5"
                    sx={{
                        mb: is4K ? 4 : is2K ? 3 : 2,
                        fontSize: is4K ? '2rem' : is2K ? '1.5rem' : '1.125rem',
                        color: theme.palette.text.secondary,
                        fontWeight: 400,
                        lineHeight: 1.6
                    }}
                >
                    {getBrandName()} is currently undergoing scheduled maintenance.
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        fontSize: is4K ? '1.6rem' : is2K ? '1.2rem' : '1rem',
                        color: theme.palette.text.secondary,
                        lineHeight: 1.7
                    }}
                >
                    We will be back shortly.
                    Thank you for your patience.
                </Typography>
            </ResponsiveCard>
        </Box>
    );
};

export default Maintenance;
